import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';

import { EmptyTab } from '@/components/EmptyTab';
import { InboxListError, getInboxListErrorType } from '@/components/InboxListError';
import { InquiryCardErrorBoundary } from '@/components/InquiryCardErrorBoundary';
import { TabRootFlatList, TabRootListFallback } from '@/components/TabRootList';
import { InboxAnnouncementBanner } from '@/components/inbox/InboxAnnouncementBanner';
import { InboxRowShell } from '@/components/inbox/InboxRowShell';
import { InquiryCard } from '@/components/inbox/InquiryCard';
import { MessageCardSkeleton } from '@/components/inbox/MessageCardSkeleton';
import { QuestionGroupCard } from '@/components/inbox/QuestionGroupCard';
import { LOAD_MORE_SCROLL_THRESHOLD } from '@/components/inbox/constants';
import { type SceneProps } from '@/components/inbox/types';
import { SupportThreadCard } from '@/components/supportThread/SupportThreadCard';
import { Divider } from '@/components/ui/divider';
import { FlatList } from '@/components/ui/flat-list';
import { RefreshControl } from '@/components/ui/refresh-control';
import { Spinner } from '@/components/ui/spinner';
import { VStack } from '@/components/ui/vstack';
import { INBOX_ANNOUNCEMENT_BANNER } from '@/constants/inboxFeatureBanners';
import { useFillGroupedInboxList } from '@/hooks/inbox/useFillGroupedInboxList';
import {
  useAnonymousInquiryGroupingEnabled,
  useGroupedInquiriesEnabled,
} from '@/hooks/inbox/useGroupedInquiriesEnabled';
import { useInboxAnnouncementBanner } from '@/hooks/inbox/useInboxAnnouncementBanner';
import { useInboxGrouping } from '@/hooks/inbox/useInboxGrouping';
import {
  useInboxQuestionGroupCardsEnabled,
  useInboxQuestionGroups,
} from '@/hooks/inbox/useInboxQuestionGroups';
import { getAllInquiriesV2QueryOptions, type InquiryV2 } from '@/services/getInquiriesV2';
import { getInquiryCancerTypesQueryOptions } from '@/services/getInquiryCancerTypes';
import { userIdAtom } from '@/services/globalState';
import { shouldFetchUserScoped } from '@/util/authTokens';
import { getCancerFocusId } from '@/util/getCancerFocusId';
import {
  buildInboxRows,
  getFirstMultiThreadQuestionGroupKey,
  type InboxRow,
} from '@/util/inboxRows';
import {
  buildInboxInquiryGroups,
  getFirstTabConversationId,
  getInquiryConversationIds,
  getInquiryGroupExperts,
  type InquiryGroupStateCounts,
  getInquiryGroupStateCounts,
  getUniqueInquiryLabels,
  type InquiryGroup,
} from '@/util/inquiryGroups';
import { Sentry } from '@/util/sentry';
import { getModRouteLabelsForGroup } from '@/utils/modHelpers';

export function MobileAllTab({ route }: SceneProps) {
  const selectedCancerFocus = route?.params?.selectedCancerFocus;

  const userId = useAtomValue(userIdAtom);

  const inboxGrouping = useInboxGrouping();

  const isInboxGroupedInquiriesEnabled = useGroupedInquiriesEnabled();

  const isAnonymousInquiryGroupingEnabled = useAnonymousInquiryGroupingEnabled();

  const [refreshing, setRefreshing] = useState(false);

  const { data: inquiryCancerTypesData } = useQuery({
    ...getInquiryCancerTypesQueryOptions(userId),
    enabled: shouldFetchUserScoped(userId),
  });

  const selectedCancerFocusId = getCancerFocusId(selectedCancerFocus, inquiryCancerTypesData);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
    useInfiniteQuery({
      ...getAllInquiriesV2QueryOptions(userId, selectedCancerFocusId, inboxGrouping),
      enabled: !!userId,
    });

  const allInquiries = useMemo<InquiryV2[]>(
    () => (data?.pages?.flatMap((page) => page?.inquiries ?? []) ?? []) as InquiryV2[],
    [data?.pages]
  );

  const isCardList = useInboxQuestionGroupCardsEnabled();

  const { questionGroups, remaining } = useInboxQuestionGroups(allInquiries, data?.pages);

  const groupedInquiries = useMemo<InquiryGroup[]>(() => {
    return buildInboxInquiryGroups(
      remaining,
      isInboxGroupedInquiriesEnabled,
      userId,
      isAnonymousInquiryGroupingEnabled
    );
  }, [remaining, isInboxGroupedInquiriesEnabled, userId, isAnonymousInquiryGroupingEnabled]);

  const rows = useMemo(
    () => buildInboxRows(groupedInquiries, data?.pages ?? [], questionGroups),
    [groupedInquiries, data?.pages, questionGroups]
  );

  useFillGroupedInboxList({
    enabled: questionGroups.length > 0,
    pages: data?.pages,
    rowCount: rows.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const tipQuestionGroupKey = useMemo(() => getFirstMultiThreadQuestionGroupKey(rows), [rows]);

  const { isVisible: isAnnouncementBannerVisible, dismiss: dismissAnnouncementBanner } =
    useInboxAnnouncementBanner({
      enabled: isInboxGroupedInquiriesEnabled,
      userId,
      bannerConfig: INBOX_ANNOUNCEMENT_BANNER,
    });

  const shouldShowAnnouncementBanner = isAnnouncementBannerVisible;

  if (isLoading) {
    return (
      <TabRootListFallback>
        <MessageCardSkeleton />
      </TabRootListFallback>
    );
  }

  if (error) {
    return (
      <TabRootListFallback>
        <InboxListError errorType={getInboxListErrorType(error)} onRetry={() => refetch()} />
      </TabRootListFallback>
    );
  }

  if (!rows.length) {
    return (
      <TabRootListFallback>
        <EmptyTab
          tabKey="all"
          tabSelectedKey="all"
          animation="GAIN_CLARITY"
          title="Primum experts are here to help"
          description="Start an inquiry now, and receive an answer in less than 24 hours."
        />
      </TabRootListFallback>
    );
  }

  return (
    <TabRootFlatList
      className="flex-1"
      showsVerticalScrollIndicator={false}
      data={rows}
      keyExtractor={(row: InboxRow) => row.key}
      renderItem={({ item: row }: { item: InboxRow }) => {
        if (row.kind === 'supportThread') {
          return (
            <InboxRowShell>
              <SupportThreadCard entry={row.entry} currentTab="all" />
            </InboxRowShell>
          );
        }

        if (row.kind === 'questionGroup') {
          return (
            <QuestionGroupCard
              questionGroup={row.questionGroup}
              currentTab="all"
              showTip={row.key === tipQuestionGroupKey}
            />
          );
        }

        const inquiryGroup = row.group;

        const groupLeadInquiry = inquiryGroup.inquiries[0];

        const hasUnread = isInboxGroupedInquiriesEnabled
          ? inquiryGroup.unreadCount > 0
          : groupLeadInquiry.hasUnread;

        const firstTabConversationId =
          isInboxGroupedInquiriesEnabled && inquiryGroup.inquiries.length > 1
            ? (getFirstTabConversationId(inquiryGroup) ?? groupLeadInquiry.conversationId)
            : groupLeadInquiry.conversationId;

        const inquiryGroupCount = isInboxGroupedInquiriesEnabled
          ? (groupLeadInquiry.inquiryGroupSize ?? inquiryGroup.inquiries.length)
          : undefined;

        const inquiryGroupExperts = isInboxGroupedInquiriesEnabled
          ? getInquiryGroupExperts(
              inquiryGroup,
              isAnonymousInquiryGroupingEnabled,
              userId ?? undefined
            )
          : undefined;

        const inquiryGroupStateBreakdown = isInboxGroupedInquiriesEnabled
          ? getInquiryGroupStateCounts(inquiryGroup.inquiries)
          : undefined;

        const routeLabels = isInboxGroupedInquiriesEnabled
          ? getModRouteLabelsForGroup(inquiryGroup, userId ?? undefined)
          : undefined;

        return (
          <InboxRowShell>
            <InquiryCardErrorBoundary conversationId={firstTabConversationId}>
              <InquiryCard
                {...groupLeadInquiry}
                conversationId={firstTabConversationId}
                hasUnread={hasUnread}
                labels={getUniqueInquiryLabels(inquiryGroup.inquiries)}
                currentTab="all"
                groupedInquiriesEnabled={isInboxGroupedInquiriesEnabled}
                groupConversationIds={getInquiryConversationIds(inquiryGroup.inquiries)}
                inquiryGroupCount={inquiryGroupCount}
                inquiryGroupVisibleCount={inquiryGroup.inquiries.length}
                inquiryGroupStateBreakdown={inquiryGroupStateBreakdown}
                groupExperts={inquiryGroupExperts}
                routeLabels={routeLabels}
              />
            </InquiryCardErrorBoundary>
          </InboxRowShell>
        );
      }}
      ListHeaderComponent={
        <>
          {shouldShowAnnouncementBanner ? (
            <VStack className="border-b border-outline-100">
              <InboxAnnouncementBanner
                onDismiss={() => dismissAnnouncementBanner().catch(() => undefined)}
              />
            </VStack>
          ) : null}
        </>
      }
      ItemSeparatorComponent={isCardList ? null : Divider}
      refreshControl={
        !isWeb ? (
          <RefreshControl
            refreshing={refreshing}
            onRefresh={async () => {
              setRefreshing(true);

              try {
                await refetch();
              } catch (e) {
                Sentry.captureException(e, {
                  tags: { component: 'MobileAllTab', action: 'pull-to-refresh' },
                });
              } finally {
                setRefreshing(false);
              }
            }}
          />
        ) : undefined
      }
      onEndReached={() => {
        if (hasNextPage && !isFetchingNextPage) {
          fetchNextPage().catch(() => undefined);
        }
      }}
      onEndReachedThreshold={LOAD_MORE_SCROLL_THRESHOLD}
      ListFooterComponent={
        <VStack className="w-full items-center py-4">
          {isFetchingNextPage ? <Spinner size="large" /> : null}
        </VStack>
      }
    />
  );
}
